import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowLeft, ArrowRight, CheckCircle, Mail, Flame, Leaf, Waves, Calendar } from 'lucide-react';
import BreathingRoomAttribution from '../components/BreathingRoomAttribution';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const BOOKING_URL = "https://www.wellnessliving.com/rs/appointment-new/breathing_room-rws2se";

// Quiz questions
const questions = [
  {
    id: 1,
    question: "After eating a protein-rich meal (meat, eggs, fish), I usually feel:",
    options: [
      { value: 'A', text: "Satisfied and steady for hours" },
      { value: 'B', text: "Heavy or sluggish" },
      { value: 'C', text: "Fine, but not noticeably different" }
    ]
  },
  {
    id: 2,
    question: "If I skip a meal, I tend to:",
    options: [
      { value: 'A', text: "Get irritable or shaky quickly" },
      { value: 'B', text: "Feel okay for quite a while" },
      { value: 'C', text: "Notice hunger but function normally" }
    ]
  },
  {
    id: 3,
    question: "After eating sweets or refined carbohydrates:",
    options: [
      { value: 'A', text: "I feel good briefly, then crash" },
      { value: 'B', text: "I feel mostly fine" },
      { value: 'C', text: "It depends on stress levels" }
    ]
  },
  {
    id: 4,
    question: "My daily energy feels best when meals are:",
    options: [
      { value: 'A', text: "Substantial, including protein and fats" },
      { value: 'B', text: "Lighter and easier to digest" },
      { value: 'C', text: "Balanced between protein, fats, and carbohydrates" }
    ]
  },
  {
    id: 5,
    question: "After intense exercise, I crave:",
    options: [
      { value: 'A', text: "Protein and savory foods" },
      { value: 'B', text: "Fruit or something sweet" },
      { value: 'C', text: "A mix of both" }
    ]
  },
  {
    id: 6,
    question: "My body tends to:",
    options: [
      { value: 'A', text: "Build muscle relatively easily" },
      { value: 'B', text: "Stay lean without much effort" },
      { value: 'C', text: "Change depending on lifestyle" }
    ]
  },
  {
    id: 7,
    question: "When stressed, I tend to:",
    options: [
      { value: 'A', text: "Crave heavier comfort-style foods" },
      { value: 'B', text: "Lose appetite or prefer lighter foods" },
      { value: 'C', text: "Either overeat or undereat depending on the situation" }
    ]
  },
  {
    id: 8,
    question: "I sleep best when:",
    options: [
      { value: 'A', text: "I eat a solid dinner with protein and fats" },
      { value: 'B', text: "I eat lighter in the evening" },
      { value: 'C', text: "I avoid extremes and eat moderately" }
    ]
  },
  {
    id: 9,
    question: "My mental focus feels most stable when:",
    options: [
      { value: 'A', text: "I've had a protein-forward meal" },
      { value: 'B', text: "I've had lighter fare" },
      { value: 'C', text: "I've balanced both" }
    ]
  },
  {
    id: 10,
    question: "My appetite is generally:",
    options: [
      { value: 'A', text: "Strong and reliable" },
      { value: 'B', text: "Variable and sometimes subtle" },
      { value: 'C', text: "Moderate and predictable" }
    ]
  },
  {
    id: 11,
    question: "My taste preference leans toward:",
    options: [
      { value: 'A', text: "Savory and salty" },
      { value: 'B', text: "Mild and lightly seasoned" },
      { value: 'C', text: "Balanced between both" }
    ]
  },
  {
    id: 12,
    question: "My digestion feels best when meals are:",
    options: [
      { value: 'A', text: "Dense and satisfying" },
      { value: 'B', text: "Lighter and simpler" },
      { value: 'C', text: "Moderate in size and composition" }
    ]
  },
  {
    id: 13,
    question: "If I eat mostly carbohydrates at a meal:",
    options: [
      { value: 'A', text: "I feel hungry again fairly quickly" },
      { value: 'B', text: "I feel energized and light" },
      { value: 'C', text: "It depends on portion size and context" }
    ]
  },
  {
    id: 14,
    question: "My relationship to food is:",
    options: [
      { value: 'A', text: "I genuinely enjoy food and look forward to meals" },
      { value: 'B', text: "I eat more for function than pleasure" },
      { value: 'C', text: "I enjoy food but don't obsess" }
    ]
  },
  {
    id: 15,
    question: "My immune resilience tends to be:",
    options: [
      { value: 'A', text: "Strong and steady" },
      { value: 'B', text: "Sensitive or easily thrown off" },
      { value: 'C', text: "Moderate" }
    ]
  },
  {
    id: 16,
    question: "My response to caffeine is:",
    options: [
      { value: 'A', text: "I don't tolerate much without feeling wired or anxious" },
      { value: 'B', text: "I tolerate it fairly well" },
      { value: 'C', text: "It depends on sleep and stress" }
    ]
  }
];

// Result content
const results = {
  grounded: {
    title: "Grounded Burner",
    icon: Flame,
    iconColor: "text-orange-500",
    bgColor: "bg-orange-50",
    borderColor: "border-orange-200",
    rhythm: "Your system thrives on stability. Protein and healthy fats help regulate your blood sugar, energy, and mood.",
    symptoms: [
      "Energy crashes",
      "Irritability",
      "Strong cravings",
      "Afternoon fatigue"
    ],
    symptomsIntro: "When you eat too light or too carbohydrate-heavy, you may notice:",
    stressPattern: "Under stress, your system may burn through fuel quickly and crave heavier foods for grounding. Blood sugar volatility can increase when meals are inconsistent.",
    benefits: [
      "Protein within 60 minutes of waking",
      "Strength training or resistance work",
      "Longer exhales during breathwork",
      "Consistent meal timing"
    ],
    macros: {
      fats: "20%",
      protein: "45%",
      carbs: "35%"
    },
    macroNote: "Adjust based on feedback from your body."
  },
  light: {
    title: "Light & Quick",
    icon: Leaf,
    iconColor: "text-emerald-500",
    bgColor: "bg-emerald-50",
    borderColor: "border-emerald-200",
    rhythm: "Your system runs efficiently and lightly. You may feel energized by higher-quality carbohydrates and moderate protein intake.",
    symptoms: null,
    symptomsIntro: "Heavy meals may feel sluggish or overly dense.",
    stressPattern: "Under stress, you may lose appetite, feel wired but tired, or experience nervous tension.",
    benefits: [
      "Smaller but consistent meals",
      "Gentle movement",
      "Stable protein intake at each meal",
      "Blood sugar awareness"
    ],
    macros: {
      carbs: "70%",
      protein: "20%",
      fats: "10%"
    },
    macroNote: "Observe how stress shifts your hunger cues."
  },
  balanced: {
    title: "Steady Balanced",
    icon: Waves,
    iconColor: "text-blue-500",
    bgColor: "bg-blue-50",
    borderColor: "border-blue-200",
    rhythm: "You adapt well. Your system performs best with balanced meals that include protein, healthy fats, and carbohydrates in moderate proportions.",
    symptoms: null,
    symptomsIntro: "Your fluctuations are often more stress-driven than macro-driven.",
    stressPattern: "Under stress, your rhythm may swing toward extremes — craving heavier foods or skipping meals altogether. Consistency stabilizes you.",
    benefits: null,
    macros: {
      protein: "40%",
      fats: "10%",
      carbs: "50%"
    },
    macroNote: "Maintain predictable meal timing to support regulation."
  }
};

const MetabolicQuizPage = () => {
  const [stage, setStage] = useState('landing'); // landing, quiz, email, result
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [email, setEmail] = useState('');
  const [result, setResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const calculateResult = () => {
    let countA = 0, countB = 0, countC = 0;
    
    Object.values(answers).forEach(answer => {
      if (answer === 'A') countA++;
      else if (answer === 'B') countB++;
      else if (answer === 'C') countC++;
    });

    // If two are within 2 points of each other, or C is dominant -> Steady Balanced
    const max = Math.max(countA, countB, countC);
    const counts = [countA, countB, countC].sort((a, b) => b - a);
    
    if (counts[0] - counts[1] <= 2 || countC === max) {
      return 'balanced';
    } else if (countA === max) {
      return 'grounded';
    } else {
      return 'light';
    }
  };

  const handleAnswer = (value) => {
    setAnswers({ ...answers, [currentQuestion]: value });
  };

  const handleNext = () => {
    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
    } else {
      setStage('email');
    }
  };

  const handlePrevious = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1);
    }
  };

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    const calculatedResult = calculateResult();
    
    // Save to backend
    try {
      await fetch(`${BACKEND_URL}/api/quiz-results`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          quiz_type: 'metabolic_rhythm',
          result: calculatedResult,
          answers,
          created_at: new Date().toISOString()
        })
      });
    } catch (error) {
      console.error('Error saving quiz result:', error);
    }
    
    setResult(calculatedResult);
    setStage('result');
    setSubmitting(false);
  };

  const progress = ((currentQuestion + 1) / questions.length) * 100;
  const resultData = result ? results[result] : null;
  const ResultIcon = resultData?.icon;

  return (
    <div className="bg-cream min-h-screen pt-24" data-testid="metabolic-quiz-page">
      <div className="max-w-3xl mx-auto px-4 md:px-8 py-6 md:py-8">
        
        {/* Back Link */}
        <Link
          to="/programs"
          className="inline-flex items-center gap-2 text-forest/70 hover:text-forest transition-colors mb-8"
        >
          <ArrowLeft size={18} />
          <span className="font-body text-sm">Back to Programs</span>
        </Link>

        <AnimatePresence mode="wait">
          {/* LANDING PAGE */}
          {stage === 'landing' && (
            <motion.div
              key="landing"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="text-center"
            >
              <p className="text-terracotta font-body text-sm tracking-widest uppercase mb-4">
                SLOW WELLNESS Quiz
              </p>
              <h1 className="font-heading text-4xl md:text-5xl text-forest leading-tight mb-4">
                Discover Your Metabolic Rhythm
              </h1>
              <p className="text-stone-600 font-body text-lg mb-8 max-w-2xl mx-auto">
                How your nervous system prefers to be fueled — and how stress may be shaping your hunger, cravings, and energy.
              </p>

              <div className="bg-white rounded-2xl p-8 shadow-lg mb-8 text-left">
                <p className="text-stone-600 font-body leading-relaxed mb-6">
                  This short quiz helps you identify your natural metabolic rhythm and how your stress response may be influencing your food preferences, energy stability, and recovery patterns.
                </p>
                
                <div className="bg-terracotta/10 rounded-xl p-4 mb-6">
                  <p className="text-forest font-body font-medium">This is not about dieting.</p>
                  <p className="text-stone-600 font-body">It's about understanding how your body actually responds.</p>
                </div>

                <p className="text-stone-600 font-body leading-relaxed mb-6">
                  Answer based on how you truly feel — not how you think you "should" eat.
                </p>

                <p className="text-stone-500 font-body text-sm italic">
                  Enter your email at the end to receive your personalized results.
                </p>
              </div>

              <button
                onClick={() => setStage('quiz')}
                className="bg-forest text-cream px-8 py-4 rounded-full font-body font-medium hover:bg-forest-dark transition-colors inline-flex items-center gap-2"
              >
                Start Quiz
                <ArrowRight size={18} />
              </button>
            </motion.div>
          )}

          {/* QUIZ QUESTIONS */}
          {stage === 'quiz' && (
            <motion.div
              key="quiz"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
            >
              {/* Progress Bar */}
              <div className="mb-8">
                <div className="flex justify-between text-sm font-body text-stone-500 mb-2">
                  <span>Question {currentQuestion + 1} of {questions.length}</span>
                  <span>{Math.round(progress)}% complete</span>
                </div>
                <div className="h-2 bg-stone-200 rounded-full overflow-hidden">
                  <motion.div
                    className="h-full bg-terracotta rounded-full"
                    initial={{ width: 0 }}
                    animate={{ width: `${progress}%` }}
                    transition={{ duration: 0.3 }}
                  />
                </div>
              </div>

              {/* Instructions (first question only) */}
              {currentQuestion === 0 && (
                <div className="bg-stone-100 rounded-xl p-3 mb-4">
                  <p className="text-stone-600 font-body text-sm">
                    Choose the answer that most consistently reflects your experience. If two feel true, choose the one that feels most common for you right now.
                  </p>
                </div>
              )}

              {/* Question */}
              <div className="bg-white rounded-2xl p-5 md:p-6 shadow-md mb-4">
                <h2 className="font-heading text-base sm:text-lg text-forest mb-4 leading-snug">
                  {questions[currentQuestion].question}
                </h2>

                <div className="space-y-2">
                  {questions[currentQuestion].options.map((option) => (
                    <button
                      key={option.value}
                      onClick={() => handleAnswer(option.value)}
                      className={`w-full text-left p-2.5 sm:p-3 rounded-lg border-2 transition-all ${
                        answers[currentQuestion] === option.value
                          ? 'border-terracotta bg-terracotta/10'
                          : 'border-stone-200 hover:border-stone-300 bg-white'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                          answers[currentQuestion] === option.value
                            ? 'border-terracotta bg-terracotta'
                            : 'border-stone-300'
                        }`}>
                          {answers[currentQuestion] === option.value && (
                            <CheckCircle size={12} className="text-white" />
                          )}
                        </div>
                        <span className="font-body text-sm text-forest">{option.text}</span>
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Navigation */}
              <div className="flex justify-between">
                <button
                  onClick={handlePrevious}
                  disabled={currentQuestion === 0}
                  className={`px-4 py-2.5 rounded-full font-body text-sm font-medium transition-colors inline-flex items-center gap-2 ${
                    currentQuestion === 0
                      ? 'bg-stone-100 text-stone-400 cursor-not-allowed'
                      : 'bg-stone-200 text-forest hover:bg-stone-300'
                  }`}
                >
                  <ArrowLeft size={16} />
                  Previous
                </button>

                <button
                  onClick={handleNext}
                  disabled={!answers[currentQuestion]}
                  className={`px-4 py-2.5 rounded-full font-body text-sm font-medium transition-colors inline-flex items-center gap-2 ${
                    !answers[currentQuestion]
                      ? 'bg-stone-200 text-stone-400 cursor-not-allowed'
                      : 'bg-forest text-cream hover:bg-forest-dark'
                  }`}
                >
                  {currentQuestion === questions.length - 1 ? 'See Results' : 'Next'}
                  <ArrowRight size={16} />
                </button>
              </div>
            </motion.div>
          )}

          {/* EMAIL CAPTURE */}
          {stage === 'email' && (
            <motion.div
              key="email"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="text-center"
            >
              <div className="bg-white rounded-2xl p-8 shadow-lg">
                <div className="w-16 h-16 bg-terracotta/10 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Mail size={32} className="text-terracotta" />
                </div>
                
                <h2 className="font-heading text-2xl text-forest mb-4">
                  Almost There!
                </h2>
                <p className="text-stone-600 font-body mb-8">
                  Enter your email to receive your personalized metabolic rhythm results and practical next steps.
                </p>

                <form onSubmit={handleEmailSubmit} className="max-w-md mx-auto">
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="your@email.com"
                    required
                    className="w-full px-4 py-3 rounded-xl border border-stone-300 font-body text-forest focus:outline-none focus:ring-2 focus:ring-terracotta/50 mb-4"
                  />
                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full bg-forest text-cream px-6 py-3 rounded-full font-body font-medium hover:bg-forest-dark transition-colors disabled:opacity-50"
                  >
                    {submitting ? 'Processing...' : 'Get My Results'}
                  </button>
                </form>
              </div>
            </motion.div>
          )}

          {/* RESULTS */}
          {stage === 'result' && resultData && (
            <motion.div
              key="result"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
            >
              {/* Result Header */}
              <div className={`${resultData.bgColor} ${resultData.borderColor} border-2 rounded-2xl p-8 mb-8 text-center`}>
                <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
                  <ResultIcon size={40} className={resultData.iconColor} />
                </div>
                <h1 className="font-heading text-3xl md:text-4xl text-forest mb-2">
                  {resultData.title}
                </h1>
                <p className="text-stone-500 font-body text-sm uppercase tracking-wider">
                  Your Metabolic Rhythm
                </p>
              </div>

              {/* A/B/C answer tally */}
              <div className="flex justify-center gap-3 mb-8" data-testid="metabolic-tally">
                {['A', 'B', 'C'].map((letter) => {
                  const count = Object.values(answers).filter((v) => v === letter).length;
                  return (
                    <div key={letter} className="bg-white border border-stone-200 rounded-xl px-6 py-3 text-center shadow-sm">
                      <p className="font-heading text-2xl text-forest">{count}</p>
                      <p className="font-body text-xs text-stone-500 uppercase tracking-wide">{letter}'s</p>
                    </div>
                  );
                })}
              </div>

              {/* Main Content */}
              <div className="bg-white rounded-2xl p-8 shadow-lg mb-8">
                <p className="text-stone-600 font-body text-lg leading-relaxed mb-6">
                  {resultData.rhythm}
                </p>

                {resultData.symptoms && (
                  <div className="mb-6">
                    <p className="text-forest font-body font-medium mb-3">{resultData.symptomsIntro}</p>
                    <ul className="space-y-2">
                      {resultData.symptoms.map((symptom, idx) => (
                        <li key={idx} className="flex items-center gap-2 text-stone-600 font-body">
                          <span className="w-2 h-2 bg-terracotta rounded-full" />
                          {symptom}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {!resultData.symptoms && resultData.symptomsIntro && (
                  <p className="text-stone-600 font-body mb-6 italic">
                    {resultData.symptomsIntro}
                  </p>
                )}

                {/* Stress Pattern */}
                <div className="bg-stone-100 rounded-xl p-6 mb-6">
                  <h3 className="font-heading text-lg text-forest mb-2">Your Stress Pattern</h3>
                  <p className="text-stone-600 font-body">
                    {resultData.stressPattern}
                  </p>
                </div>

                {/* Benefits */}
                {resultData.benefits && (
                  <div className="mb-6">
                    <h3 className="font-heading text-lg text-forest mb-3">You benefit from:</h3>
                    <ul className="space-y-2">
                      {resultData.benefits.map((benefit, idx) => (
                        <li key={idx} className="flex items-center gap-2 text-stone-600 font-body">
                          <CheckCircle size={16} className="text-emerald-500" />
                          {benefit}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Macros */}
                <div className="bg-forest text-cream rounded-xl p-6">
                  <h3 className="font-heading text-lg text-terracotta mb-4">Starting Macro Rhythm</h3>
                  <div className="grid grid-cols-3 gap-4 mb-4">
                    {resultData.macros.protein && (
                      <div className="text-center">
                        <p className="font-heading text-2xl">{resultData.macros.protein}</p>
                        <p className="font-body text-cream/70 text-sm">Protein</p>
                      </div>
                    )}
                    {resultData.macros.fats && (
                      <div className="text-center">
                        <p className="font-heading text-2xl">{resultData.macros.fats}</p>
                        <p className="font-body text-cream/70 text-sm">Healthy Fats</p>
                      </div>
                    )}
                    {resultData.macros.carbs && (
                      <div className="text-center">
                        <p className="font-heading text-2xl">{resultData.macros.carbs}</p>
                        <p className="font-body text-cream/70 text-sm">Carbohydrates</p>
                      </div>
                    )}
                  </div>
                  <p className="text-cream/80 font-body text-sm italic text-center">
                    {resultData.macroNote}
                  </p>
                </div>
              </div>

              {/* CTA Section */}
              <div className="bg-gradient-to-br from-terracotta/10 to-amber-50 rounded-2xl p-8 text-center">
                <h2 className="font-heading text-2xl text-forest mb-4">
                  Ready to Explore Deeper?
                </h2>
                <p className="text-stone-600 font-body mb-2">
                  A quiz offers insight. A conversation offers clarity.
                </p>
                <p className="text-stone-600 font-body mb-6">
                  Book a <strong>Metabolic Exploration Session</strong> and we will assess:
                </p>

                <div className="grid md:grid-cols-2 gap-3 max-w-lg mx-auto mb-8 text-left">
                  {[
                    'Your stress physiology',
                    'Your digestion patterns',
                    'Your movement habits',
                    'Your energy cycles',
                    'Your real-life food rhythm'
                  ].map((item, idx) => (
                    <div key={idx} className="flex items-center gap-2">
                      <CheckCircle size={16} className="text-terracotta" />
                      <span className="font-body text-stone-600 text-sm">{item}</span>
                    </div>
                  ))}
                </div>

                <p className="text-forest font-body font-medium mb-6">
                  This is personalized work. Not guesswork.
                </p>

                <a
                  href={BOOKING_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-forest text-cream px-8 py-4 rounded-full font-body font-medium hover:bg-forest-dark transition-colors inline-flex items-center gap-2"
                >
                  <Calendar size={18} />
                  Book Your Exploration Session
                </a>
                <div className="mt-5 flex justify-center">
                  <BreathingRoomAttribution align="center" />
                </div>
              </div>

              {/* Retake Quiz */}
              <div className="text-center mt-8">
                <button
                  onClick={() => {
                    setStage('landing');
                    setCurrentQuestion(0);
                    setAnswers({});
                    setEmail('');
                    setResult(null);
                  }}
                  className="text-stone-500 font-body text-sm hover:text-forest transition-colors"
                >
                  Retake Quiz
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default MetabolicQuizPage;
